import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { reinitCartDeliveryWidgets } from './cart-discount.js';

/**
 * VJ: aplica automáticamente el código de descuento del newsletter (10%) al carrito
 * en cuanto detecta que Shopify Forms marcó al visitante como suscrito.
 *
 * Shopify Forms guarda el código en la cookie `discount_code` cuando alguien se
 * suscribe (esto ya lo hace de forma nativa para aplicarlo en el checkout). Aquí lo
 * leemos y lo aplicamos de inmediato al carrito usando el mismo endpoint que usa el
 * campo manual de "Descuento" (`cart-discount.js`), para que el cliente lo vea puesto
 * en el carrito lateral sin tener que llegar al checkout.
 */

const COOKIE_NAME = 'discount_code';
const DISMISSED_KEY = 'vj-newsletter-discount-dismissed';

/**
 * Lee el valor de una cookie por nombre.
 * @param {string} name
 * @returns {string | null}
 */
function getCookie(name) {
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Lee los códigos de descuento ya aplicados/visibles en el carrito renderizado.
 * @returns {string[]}
 */
function getExistingDiscountCodes() {
  const pills = document.querySelectorAll('.cart-discount__pill[data-discount-code]');
  return Array.from(pills)
    .map((pill) => (pill instanceof HTMLElement ? pill.dataset.discountCode : null))
    .filter((code) => typeof code === 'string');
}

/**
 * Aplica el código de descuento del newsletter al carrito, si corresponde.
 */
async function applyNewsletterDiscount() {
  const code = getCookie(COOKIE_NAME);
  if (!code) return;

  // El cliente quitó este código manualmente en esta sesión: respetar su decisión.
  if (sessionStorage.getItem(DISMISSED_KEY) === code) return;

  const discountComponent = document.querySelector('cart-discount-component[data-section-id]');
  const sectionId = discountComponent instanceof HTMLElement ? discountComponent.dataset.sectionId : null;
  if (!sectionId) return;

  const existing = getExistingDiscountCodes();
  if (existing.includes(code)) return;

  try {
    const config = fetchConfig('json', {
      body: JSON.stringify({
        discount: [...existing, code].join(','),
        sections: [sectionId],
      }),
    });

    const response = await fetch(Theme.routes.cart_update_url, config);
    const data = await response.json();

    const applied = data.discount_codes?.find(
      (/** @type {{ code: string; applicable: boolean }} */ discount) =>
        discount.code === code && discount.applicable
    );
    if (!applied) return;

    document.dispatchEvent(new DiscountUpdateEvent(data, 'venus-newsletter-discount'));
    await morphSection(sectionId, data.sections[sectionId]);

    // Sin esto, la fecha estimada de entrega y el selector de envío se quedan
    // vacíos tras aplicar el descuento automáticamente (mismo fix que en
    // cart-discount.js, ver comentarios ahí).
    document.dispatchEvent(new CustomEvent('cart:html-updated'));
    reinitCartDeliveryWidgets();
  } catch (error) {
    // Silencioso: si falla, Shopify Forms de todas formas aplica el código en el
    // checkout de forma nativa, así que no rompemos nada mostrando un error aquí.
  }
}

// Si quitan manualmente el código del newsletter, no lo volvamos a poner en esta sesión.
document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const removeButton = target.closest('.cart-discount__pill-remove');
  if (!removeButton) return;

  const pill = removeButton.closest('.cart-discount__pill');
  const removedCode = pill instanceof HTMLElement ? pill.dataset.discountCode : null;
  if (removedCode && removedCode === getCookie(COOKIE_NAME)) {
    sessionStorage.setItem(DISMISSED_KEY, removedCode);
  }
});

// Intentarlo apenas carga la página (por si ya estaba suscrito de antes)...
applyNewsletterDiscount();

// ...y cada vez que se abre o se actualiza el carrito (por si se suscribió en esta sesión).
document.addEventListener('cart:open', applyNewsletterDiscount);
document.addEventListener('cart:update', applyNewsletterDiscount);
